import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getProducts } from './services/firebaseService';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

const categories = [
  'Others',
  'Welding Machines',
  'Accessories',
  'Power Tools',
  'Generator',
  'Air Compressors',
  'Home Improvements',
  'Machinery Service Consultant',
];

const getCategoryLabel = (category, translate) => {
  const categoryTranslationKeys = {
    'Welding Machines': 'products.weldingMachines',
    Accessories: 'products.accessories',
    'Power Tools': 'products.powerTools',
    Generator: 'products.generator',
    'Air Compressors': 'products.airCompressors',
    'Home Improvements': 'products.homeImprovements',
    Others: 'products.promotions',
    Promotion: 'products.promotions',
    'Machinery Service Consultant': 'products.machineryService'
  };
  const key = categoryTranslationKeys[category];
  return key ? translate(key) : category;
};

// Sub-categories mapping for applicable categories
const subCategories = {
  'Welding Machines': ['MIG', 'ARC', 'TIG', 'Plasma Cutter', 'Other'],
  'Power Tools': ['Amaxx', 'Dong Cheng', 'Other'],
  'Home Improvements': ['Karcher', 'Other'],
};

const Products = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [shippingFeesLoading] = useState(false);
  const [shippingFeesConfig] = useState({
    westMalaysia: { firstKg: 10, additionalKg: 3 },
    eastMalaysia: { firstKg: 10, additionalKg: 12 }
  });

  // Reset sub-category whenever main category changes
  useEffect(() => {
    setSelectedSubCategory('');
  }, [selectedCategory]);

  // Format price helper function
  const parsePrice = (price) => {
    if (price === '' || price == null) return NaN;
    const numPrice = parseFloat(price.toString().replace(/[^\d.]/g, ''));
    return Number.isNaN(numPrice) ? NaN : numPrice;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = parsePrice(price);
    if (Number.isNaN(numPrice)) return price;
    return `RM ${numPrice.toFixed(2)}`;
  };

  const getDiscountPercent = (normalPrice, sellingPrice) => {
    const normal = parsePrice(normalPrice);
    const selling = parsePrice(sellingPrice);
    if (Number.isNaN(normal) || Number.isNaN(selling) || normal <= 0 || normal <= selling) {
      return 0;
    }
    return Math.round(((normal - selling) / normal) * 100);
  };

  const hasNormalPrice = (normalPrice) =>
    normalPrice !== undefined && normalPrice !== null && String(normalPrice).trim() !== '';

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const result = await getProducts();
    if (result.success) {
      setProducts(result.products);
    } else {
      setError(t('products.loadFailed', { error: result.error }));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts = products.filter((product) => {
    // Hide products explicitly disabled by admin (enabled or unset = shown)
    if (product.status === 'disable') return false;
    if (product.category !== selectedCategory) return false;
    const subCats = subCategories[selectedCategory] || [];
    if (selectedSubCategory && subCats.length > 0) {
      return product.subCategory === selectedSubCategory;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Sub-header - Fixed positioned */}
      {/* <div className="fixed top-20 left-0 right-0 bg-gray-50 border-b border-gray-200 py-3 z-40">
        <div className="max-w-7xl mx-auto px-2 md:px-8">
          <div className="flex justify-end space-x-4">
            <button 
              onClick={() => navigate('/check-order-status')}
              className="flex items-center space-x-2 text-primary hover:text-blue-800 font-semibold transition-colors"
            >
              <AssignmentIcon className="!w-5 !h-5" />
              <span>{t('subHeader.checkOrderStatus')}</span>
            </button>
            <button 
              onClick={openShippingPolicyModal}
              className="flex items-center space-x-2 text-primary hover:text-blue-800 font-semibold transition-colors"
            >
              <LocalShippingIcon className="!w-5 !h-5" />
              <span>{t('subHeader.shippingPolicy')}</span>
            </button>
            <button 
              onClick={() => navigate('/cart')}
              className="flex items-center space-x-2 text-primary hover:text-blue-800 font-semibold transition-colors relative"
            >
              <ShoppingCartIcon className="!w-5 !h-5" />
              <span>{t('subHeader.cart')}</span>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div> */}

      <div className="flex-1 flex flex-col md:flex-row">
        <div className="md:hidden border-b border-gray-200 bg-white px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t('products.categories')}
          </p>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 snap-x snap-mandatory">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`shrink-0 snap-start rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-white'
                    : 'border border-gray-200 bg-white text-gray-700'
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                {getCategoryLabel(cat, t)}
              </button>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="hidden h-[calc(100vh-72px)] w-64 shrink-0 flex-col gap-4 border-r border-gray-200 bg-white p-4 md:sticky md:top-[72px] md:ml-8 md:flex">
          <div className="mb-2">
            <h2 className="text-xl font-bold text-primary">{t('products.categories')}</h2>
            
          </div>

          <nav className="flex flex-1 flex-col gap-2 overflow-y-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`group flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-semibold transition-all duration-150 active:translate-x-1 ${
                  selectedCategory === cat
                    ? 'bg-primary text-white'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                {getCategoryLabel(cat, t)}
              </button>
            ))}
          </nav>

        </aside>
        {/* Products listing */}
        <main className="flex-1">
          <div className="max-w-7xl mx-auto px-4 md:px-0 py-6 md:py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-primary mb-4">{getCategoryLabel(selectedCategory, t)}</h1>
            {(subCategories[selectedCategory] && subCategories[selectedCategory].length > 0) && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors
                      ${selectedSubCategory === '' ? 'bg-primary text-white border-primary' : 'bg-white text-primary border-primary/50 hover:bg-primary/10'}`}
                    onClick={() => setSelectedSubCategory('')}
                  >
                    {t('products.all')}
                  </button>
                  {subCategories[selectedCategory].map((subCat) => (
                    <button
                      key={subCat}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors
                        ${selectedSubCategory === subCat ? 'bg-primary text-white border-primary' : 'bg-white text-primary border-primary/50 hover:bg-primary/10'}`}
                      onClick={() => setSelectedSubCategory(subCat)}
                    >
                      {subCat}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">{t('products.loadingProducts')}</div>
              </div>
            ) : selectedCategory === 'Machinery Service Consultant' ? (
              <div className="bg-primary/5 border border-primary/10 rounded-2xl p-8 text-center max-w-2xl mx-auto shadow">
                <h2 className="text-xl md:text-2xl font-bold text-primary mb-4">{t('products.machineryService')}</h2>
                <p className="text-gray-700 mb-4">{t('products.serviceDescription')}</p>
                <p className="text-gray-700 mb-6">{t('products.serviceContact')}</p>
                <a href="https://wa.me/60167387329" target="_blank" rel="noopener noreferrer" className="inline-block bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all">{t('products.contactWhatsApp')}</a>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
                {filteredProducts.map((product) => {
                  const showNormalPrice = hasNormalPrice(product.normalPrice);
                  const discountPercent = getDiscountPercent(product.normalPrice, product.price);
                  const cardLabel = getCategoryLabel(
                    (product.subCategory || product.category || 'Promotion').toString(),
                    t
                  );
                  return (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="group flex h-[28rem] w-full max-w-xs flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:shadow-lg"
                    >
                      <div className="relative flex h-52 items-center justify-center overflow-hidden border-b border-gray-200 bg-gray-100 p-4">
                        {discountPercent > 0 && (
                          <span className="absolute left-3 top-3 z-10 rounded bg-red-500 px-2 py-1 text-xs font-bold text-white shadow-sm">
                            -{discountPercent}%
                          </span>
                        )}
                        <img
                          src={product.image}
                          alt={product.name}
                          className="h-full object-contain mix-blend-darken transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex h-[14rem] flex-col p-4">
                        <div className="h-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                          {cardLabel}
                        </div>
                        <h3 className="mb-3 mt-2 h-[5.5rem] overflow-hidden text-base font-bold leading-6 text-primary">{product.name}</h3>
                        <div className="mt-auto">
                          <div className="mb-4 min-h-[2rem]">
                            {showNormalPrice && (
                              <div className="text-sm text-gray-500 line-through">
                                {formatPrice(product.normalPrice)}
                              </div>
                            )}
                            <div className="text-2xl font-bold leading-8 text-primary">
                              {formatPrice(product.price)}
                            </div>
                          </div>
                          <span className="flex h-10 w-full items-center justify-center gap-2 rounded-md border-2 border-primary px-4 font-semibold text-primary transition-colors hover:bg-primary hover:text-white">
                            {t('products.buyNow')}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
            {!loading && filteredProducts.length === 0 && selectedCategory !== 'Machinery Service Consultant' && (
              <div className="text-center text-gray-500 py-8">
                {t('products.noProductsInCategory')}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Shipping Policy Modal */}
      {showShippingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowShippingModal(false)}>
          <div className="bg-white rounded-2xl p-4 md:p-8 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">{t('shipping.title')}</h2>
              <button 
                onClick={() => setShowShippingModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={t('common.close')}
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {shippingFeesLoading && (
                <p className="text-gray-500 text-sm">{t('shipping.refreshing')}</p>
              )}
              <div>
                <h3 className="text-lg font-semibold text-primary mb-3">{t('shipping.westMalaysia')}</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">{t('shipping.standardDelivery')}</h4>
                  <p className="text-blue-700 text-sm">
                    {t('shipping.firstKg', { amount: Number(shippingFeesConfig.westMalaysia.firstKg).toFixed(2) })}
                  </p>
                  <p className="text-blue-700 text-sm">
                    {t('shipping.additionalKg', { amount: Number(shippingFeesConfig.westMalaysia.additionalKg).toFixed(2) })}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-primary mb-3">{t('shipping.eastMalaysia')}</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">{t('shipping.standardDelivery')}</h4>
                  <p className="text-blue-700 text-sm">
                    {t('shipping.firstKg', { amount: Number(shippingFeesConfig.eastMalaysia.firstKg).toFixed(2) })}
                  </p>
                  <p className="text-blue-700 text-sm">
                    {t('shipping.additionalKg', { amount: Number(shippingFeesConfig.eastMalaysia.additionalKg).toFixed(2) })}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <button 
                onClick={() => setShowShippingModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-semibold"
              >
                {t('shipping.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;